/**
 * Planning hebdomadaire du formateur.
 *
 * - Affiche les séances de la semaine groupées par jour
 * - Alerte visible en haut si un cours commence dans < 24h (rouge si < 2h)
 * - Bouton « Déclarer une absence » qui ouvre un modal
 */
import { useMemo, useState, useRef, useEffect } from 'react';
import {
  Calendar, AlertTriangle, Loader2, Clock, MapPin, GraduationCap,
  UserX, CheckCircle2, ChevronLeft, ChevronRight, X,
  MoreVertical, CalendarClock, XCircle, BookOpen, Send,
} from 'lucide-react';
import { FormateurLayout } from '../FormateurLayout';
import {
  useMonPlanningQuery, useDeclarerAbsenceMutation,
  useAnnulerSeanceMutation, useReporterSeanceFormateurMutation,
  type PlanningSeanceItem,
} from '../../services/formateurApi';

const JOURS_LABEL: Record<string, string> = {
  Monday: 'Lundi', Tuesday: 'Mardi', Wednesday: 'Mercredi',
  Thursday: 'Jeudi', Friday: 'Vendredi', Saturday: 'Samedi', Sunday: 'Dimanche',
};

function dateISO(d: Date): string { return d.toISOString().slice(0, 10); }
function debutSemaine(ref: Date): Date {
  const d = new Date(ref);
  const jour = d.getDay(); // 0=dim, 1=lun
  const diff = (jour === 0 ? -6 : 1 - jour);
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

export default function Planning() {
  const [refDate, setRefDate] = useState<Date>(new Date());
  const [showDeclarer, setShowDeclarer] = useState<PlanningSeanceItem | 'libre' | null>(null);
  const [showReporter, setShowReporter] = useState<PlanningSeanceItem | null>(null);

  const debut = useMemo(() => debutSemaine(refDate), [refDate]);
  const fin = useMemo(() => { const d = new Date(debut); d.setDate(d.getDate() + 6); return d; }, [debut]);

  const { data, isLoading, refetch } = useMonPlanningQuery({
    depuis: dateISO(debut), jusqua: dateISO(fin),
  });

  const grouped = useMemo(() => {
    const map: Record<string, PlanningSeanceItem[]> = {};
    (data?.results || []).forEach(s => {
      (map[s.date] ||= []).push(s);
    });
    return map;
  }, [data]);

  const joursSemaine: { date: string; label: string }[] = useMemo(() => {
    const out: { date: string; label: string }[] = [];
    for (let i = 0; i < 6; i++) { // L-S (pas dimanche)
      const d = new Date(debut);
      d.setDate(d.getDate() + i);
      const date = dateISO(d);
      const label = d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' });
      out.push({ date, label });
    }
    return out;
  }, [debut]);

  return (
    <FormateurLayout title="Mon planning">
      {/* Alerte prochain cours */}
      {data?.prochain_cours && (
        <div className={`rounded-xl p-3 mb-3 border shadow-sm ${
          data.alerte_imminente
            ? 'bg-red-500 border-red-600 text-white dark:bg-red-900/40 dark:border-red-700 dark:text-red-100 animate-pulse'
            : 'bg-amber-100 border-amber-300 text-amber-800 dark:bg-amber-900/30 dark:border-amber-700/60 dark:text-amber-200'
        }`}>
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-sm">
                Prochain cours dans {Math.floor(data.prochain_cours.dans_minutes / 60)}h
                {String(data.prochain_cours.dans_minutes % 60).padStart(2, '0')}min
              </div>
              <div className="text-xs mt-0.5">
                {data.prochain_cours.matiere} • {data.prochain_cours.classe}
              </div>
              <div className="text-xs opacity-90">
                {data.prochain_cours.heure_debut}–{data.prochain_cours.heure_fin}
                {data.prochain_cours.salle && ` • ${data.prochain_cours.salle}`}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation semaine */}
      <div className="flex items-center gap-2 mb-3 bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-xl p-2 shadow-sm dark:shadow-none">
        <button onClick={() => { const d = new Date(debut); d.setDate(d.getDate() - 7); setRefDate(d); }}
          className="p-1.5 text-slate-500 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800 rounded-lg">
          <ChevronLeft size={16} />
        </button>
        <div className="flex-1 text-center text-xs font-semibold text-slate-700 dark:text-gray-200">
          Semaine du {debut.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
          {' au '}
          {fin.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
        </div>
        <button onClick={() => { const d = new Date(debut); d.setDate(d.getDate() + 7); setRefDate(d); }}
          className="p-1.5 text-slate-500 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800 rounded-lg">
          <ChevronRight size={16} />
        </button>
        <button onClick={() => setRefDate(new Date())}
          className="px-2 py-1 text-[10px] bg-blue-100 hover:bg-blue-200 text-blue-700 dark:bg-blue-900/50 dark:hover:bg-blue-800 dark:text-blue-300 rounded font-semibold">
          Aujourd'hui
        </button>
      </div>

      {/* Bandeau info quand pas de séances générées mais EDT présent */}
      {data && data.nb_seances_reelles === 0 && (data.nb_edts_actifs || 0) > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 rounded-lg p-2.5 mb-3 text-[11px] flex items-start gap-2">
          <BookOpen size={12} className="mt-0.5 flex-shrink-0" />
          <span>
            Vos cours sont affichés depuis votre emploi du temps régulier.
            La scolarité générera les séances officielles à l'approche des examens.
          </span>
        </div>
      )}

      {/* Bouton déclarer absence */}
      <button onClick={() => setShowDeclarer('libre')}
        className="w-full mb-3 bg-gradient-to-r from-red-50 to-red-50/40 dark:from-red-900/40 dark:to-red-900/10 hover:from-red-100 dark:hover:from-red-900/50
          border border-red-300 dark:border-red-700/40 hover:border-red-400 dark:hover:border-red-500 rounded-xl p-3 flex items-center gap-3 transition-colors text-left">
        <div className="w-9 h-9 rounded-lg bg-red-500 dark:bg-black/30 flex items-center justify-center text-white dark:text-red-300 shadow-sm">
          <UserX size={18} />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-red-900 dark:text-white">Déclarer une absence</div>
          <div className="text-[11px] text-red-700 dark:text-gray-400">Notifie les étudiants, la scolarité et la surveillance</div>
        </div>
      </button>

      {isLoading && (
        <div className="text-center py-8 text-slate-500 dark:text-gray-400 text-sm">
          <Loader2 size={16} className="inline animate-spin mr-2" /> Chargement…
        </div>
      )}

      {/* Planning par jour — uniquement les jours avec cours */}
      <div className="space-y-3">
        {(() => {
          const joursAvecCours = joursSemaine.filter(j => (grouped[j.date] || []).length > 0);
          if (joursAvecCours.length === 0 && !isLoading) {
            return (
              <div className="text-center py-10 text-slate-400 dark:text-gray-500 text-sm">
                <Calendar size={32} className="inline mb-2 opacity-50" />
                <div>Aucun cours cette semaine.</div>
              </div>
            );
          }
          return joursAvecCours.map(j => {
            const seances = grouped[j.date];
            return (
              <div key={j.date}>
                <div className="flex items-center gap-2 text-xs font-bold text-slate-600 dark:text-gray-300 uppercase tracking-wider mb-1.5 px-1">
                  <Calendar size={12} className="text-amber-600 dark:text-amber-400" />
                  {j.label}
                  <span className="text-[10px] font-normal text-slate-500 dark:text-gray-500">
                    {seances.length === 1 ? '1 séance' : `${seances.length} séances`}
                  </span>
                </div>
                <div className="space-y-1.5">
                  {seances.map(s => (
                    <CarteSeance key={`${s.id}-${s.date}-${s.heure_debut}`} s={s}
                      onAbsence={() => setShowDeclarer(s)}
                      onReporter={() => setShowReporter(s)}
                      onAnnulee={refetch} />
                  ))}
                </div>
              </div>
            );
          });
        })()}
      </div>

      {showDeclarer && (
        <ModalDeclarerAbsence seance={showDeclarer === 'libre' ? null : showDeclarer}
          onClose={() => setShowDeclarer(null)}
          onDone={() => { setShowDeclarer(null); refetch(); }} />
      )}
      {showReporter && (
        <ModalReporterCours seance={showReporter}
          onClose={() => setShowReporter(null)}
          onDone={() => { setShowReporter(null); refetch(); }} />
      )}
    </FormateurLayout>
  );
}

function CarteSeance({ s, onAbsence, onReporter, onAnnulee }: {
  s: PlanningSeanceItem;
  onAbsence: () => void;
  onReporter: () => void;
  onAnnulee: () => void;
}) {
  const cancelled = s.statut === 'reportee' || s.statut === 'annulee';
  const virtuel = !!s.is_virtuel;
  const examen = !!s.is_examen;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [annuler] = useAnnulerSeanceMutation();

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const handleAnnuler = async () => {
    if (virtuel) {
      alert("Ce cours n'est pas encore généré en séance officielle — utilisez « Déclarer une absence » pour le notifier à la scolarité.");
      setMenuOpen(false);
      return;
    }
    if (!window.confirm(`Annuler le cours du ${s.date} à ${s.heure_debut} ?\n${s.matiere} • ${s.classe}`)) return;
    setMenuOpen(false);
    try {
      await annuler({ seanceId: s.id }).unwrap();
      onAnnulee();
    } catch (e: any) {
      alert(e?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className={`rounded-lg border p-2.5 flex items-start gap-2.5 transition-colors ${
      examen
        ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-300 dark:border-indigo-700/60 ring-1 ring-indigo-200 dark:ring-indigo-800/40'
        : cancelled
          ? 'bg-slate-50 dark:bg-gray-900/30 border-slate-200 dark:border-gray-800 opacity-60'
          : s.est_rattrapage
            ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/60'
            : virtuel
              ? 'bg-white dark:bg-gray-900/60 border-slate-200 dark:border-gray-800 border-dashed'
              : 'bg-white dark:bg-gray-900/60 border-slate-200 dark:border-gray-800 shadow-sm'
    }`}>
      <div className="text-center min-w-[44px]">
        <div className={`text-xs font-bold ${examen ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-900 dark:text-white'}`}>{s.heure_debut}</div>
        <div className="text-[9px] text-slate-400 dark:text-gray-500">↓</div>
        <div className="text-xs text-slate-500 dark:text-gray-400">{s.heure_fin}</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-slate-900 dark:text-white truncate flex items-center gap-1.5">
          {examen && (
            <span className="text-[9px] px-1.5 py-0.5 bg-indigo-600 text-white rounded font-bold tracking-wide">
              EXAMEN
            </span>
          )}
          {s.matiere}
          {s.est_rattrapage && !examen && (
            <span className="text-[9px] px-1 py-0.5 bg-amber-200 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 rounded">RATTR.</span>
          )}
          {examen && s.est_rattrapage && (
            <span className="text-[9px] px-1 py-0.5 bg-amber-200 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 rounded">RATTR.</span>
          )}
          {virtuel && !examen && (
            <span className="text-[9px] px-1 py-0.5 bg-slate-100 text-slate-600 dark:bg-gray-800 dark:text-gray-400 rounded" title="Cours non encore généré officiellement">
              EDT
            </span>
          )}
        </div>
        <div className="text-[11px] text-slate-500 dark:text-gray-400 truncate flex items-center gap-2 mt-0.5 flex-wrap">
          <span className="inline-flex items-center gap-0.5"><GraduationCap size={10} /> {s.classe}</span>
          {s.salle && <span className="inline-flex items-center gap-0.5"><MapPin size={10} /> {s.salle}</span>}
          {s.code_matiere && <span className="text-[10px] text-slate-400">({s.code_matiere})</span>}
        </div>
        <div className="text-[10px] mt-1">
          {examen ? (
            <span className="text-indigo-700 dark:text-indigo-300 font-semibold inline-flex items-center gap-1">
              <BookOpen size={10} /> {s.session_examen?.nom || `Session ${s.session_examen?.type || ''}`}
            </span>
          ) : cancelled ? (
            <span className="text-red-600 dark:text-red-400 font-semibold">{s.statut === 'annulee' ? '✗ Annulé' : '↻ Reporté'}</span>
          ) : s.appel_fait ? (
            <span className="text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-0.5">
              <CheckCircle2 size={9} /> Appel fait
            </span>
          ) : (
            <span className="text-slate-400 dark:text-gray-500 inline-flex items-center gap-0.5">
              <Clock size={9} /> {virtuel ? 'Cours prévu' : 'En attente'}
            </span>
          )}
        </div>
      </div>
      {!cancelled && !examen && (
        <div className="relative" ref={menuRef}>
          <button onClick={() => setMenuOpen(o => !o)}
            className="p-1.5 text-slate-400 dark:text-gray-500 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-gray-800 rounded"
            title="Actions">
            <MoreVertical size={14} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 z-20 w-44 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden">
              <button
                onClick={() => { setMenuOpen(false); onAbsence(); }}
                className="w-full px-3 py-2 text-left text-xs flex items-center gap-2 text-slate-700 dark:text-gray-200 hover:bg-amber-50 dark:hover:bg-amber-900/30">
                <UserX size={12} className="text-amber-600" /> Signaler une absence
              </button>
              <button
                onClick={() => {
                  if (virtuel) {
                    alert("Ce cours n'est pas encore généré officiellement. Demandez à la scolarité de générer les séances avant de pouvoir le reporter.");
                    setMenuOpen(false); return;
                  }
                  setMenuOpen(false); onReporter();
                }}
                className="w-full px-3 py-2 text-left text-xs flex items-center gap-2 text-slate-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-blue-900/30 border-t border-slate-100 dark:border-gray-700">
                <CalendarClock size={12} className="text-blue-600" /> Reporter le cours
              </button>
              <button
                onClick={handleAnnuler}
                className="w-full px-3 py-2 text-left text-xs flex items-center gap-2 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 border-t border-slate-100 dark:border-gray-700">
                <XCircle size={12} /> Annuler le cours
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Modal Reporter un cours vers une autre date
// ────────────────────────────────────────────────────────────────────────────

function ModalReporterCours({ seance, onClose, onDone }: {
  seance: PlanningSeanceItem; onClose: () => void; onDone: () => void;
}) {
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
  const [nouvelleDate, setNouvelleDate] = useState(tomorrow.toISOString().slice(0, 10));
  const [heureDebut, setHeureDebut] = useState(seance.heure_debut);
  const [heureFin, setHeureFin] = useState(seance.heure_fin);
  const [salle, setSalle] = useState(seance.salle || '');
  const [erreur, setErreur] = useState('');
  const [reporter, { isLoading }] = useReporterSeanceFormateurMutation();

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur('');
    if (!nouvelleDate) { setErreur('Nouvelle date requise'); return; }
    try {
      await reporter({
        seanceId: seance.id,
        nouvelle_date: nouvelleDate,
        nouvelle_heure_debut: heureDebut,
        nouvelle_heure_fin: heureFin,
        nouvelle_salle: salle || undefined,
      }).unwrap();
      onDone();
    } catch (err: any) {
      setErreur(err?.data?.detail || 'Erreur lors du report');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center"
      onClick={onClose}>
      <form onSubmit={handle} onClick={(e) => e.stopPropagation()}
        className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[92vh] overflow-y-auto shadow-xl">
        <header className="sticky top-0 bg-white dark:bg-gray-900 border-b border-slate-200 dark:border-gray-800 px-4 py-3 flex items-start justify-between">
          <div>
            <h3 className="font-bold text-base text-slate-900 dark:text-white inline-flex items-center gap-2">
              <CalendarClock size={16} className="text-blue-600" /> Reporter le cours
            </h3>
            <div className="text-[10px] text-slate-500 dark:text-gray-500 mt-0.5">
              {seance.matiere} • {seance.classe} — {seance.date} {seance.heure_debut}
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-900 dark:hover:text-white">
            <X size={18} />
          </button>
        </header>

        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-gray-300 mb-1">Nouvelle date *</label>
            <input type="date" value={nouvelleDate} onChange={(e) => setNouvelleDate(e.target.value)}
              min={new Date().toISOString().slice(0, 10)} required
              className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded text-slate-900 dark:text-gray-100" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-gray-300 mb-1">Heure début</label>
              <input type="time" value={heureDebut} onChange={(e) => setHeureDebut(e.target.value)}
                className="w-full px-2 py-1.5 text-sm bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded text-slate-900 dark:text-gray-100" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-gray-300 mb-1">Heure fin</label>
              <input type="time" value={heureFin} onChange={(e) => setHeureFin(e.target.value)}
                className="w-full px-2 py-1.5 text-sm bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded text-slate-900 dark:text-gray-100" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-gray-300 mb-1">Salle (optionnel)</label>
            <input value={salle} onChange={(e) => setSalle(e.target.value)} maxLength={50}
              placeholder={seance.salle ? `Actuelle : ${seance.salle}` : 'À préciser'}
              className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded text-slate-900 dark:text-gray-100" />
          </div>
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded p-2 text-[11px] text-amber-700 dark:text-amber-300">
            Le cours actuel sera marqué comme « reporté » et une nouvelle séance de rattrapage sera créée à la date choisie.
          </div>
          {erreur && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 rounded p-2 text-xs">
              {erreur}
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-white dark:bg-gray-900 border-t border-slate-200 dark:border-gray-800 px-4 py-3 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-xs text-slate-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-800 rounded">
            Annuler
          </button>
          <button type="submit" disabled={isLoading || !nouvelleDate}
            className="px-4 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded inline-flex items-center gap-1.5 shadow-sm">
            {isLoading ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            Reporter
          </button>
        </footer>
      </form>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Modal de déclaration d'absence
// ────────────────────────────────────────────────────────────────────────────

function ModalDeclarerAbsence({ seance, onClose, onDone }: {
  seance: PlanningSeanceItem | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [date, setDate] = useState(seance?.date || new Date().toISOString().slice(0, 10));
  const [classe, setClasse] = useState(seance?.classe || '');
  const [matiere, setMatiere] = useState(seance?.matiere || '');
  const [heureDebut, setHeureDebut] = useState(seance?.heure_debut || '');
  const [heureFin, setHeureFin] = useState(seance?.heure_fin || '');
  const [motif, setMotif] = useState('');
  const [peutRattraper, setPeutRattraper] = useState<'oui' | 'non' | 'a_decider'>('a_decider');
  const [dateRattrapage, setDateRattrapage] = useState('');
  const [erreur, setErreur] = useState('');
  const [declarer, { isLoading }] = useDeclarerAbsenceMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur('');
    if (!motif.trim()) { setErreur('Motif requis'); return; }
    try {
      const res = await declarer({
        date_absence: date,
        classe: classe || undefined,
        matiere_libelle: matiere || undefined,
        heure_debut: heureDebut || undefined,
        heure_fin: heureFin || undefined,
        motif,
        peut_rattraper: peutRattraper,
        date_rattrapage_proposee: dateRattrapage || undefined,
      }).unwrap();
      alert(res.message);
      onDone();
    } catch (err: any) {
      setErreur(err?.data?.error || err?.data?.detail || "Erreur lors de l'envoi");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}>
      <form onSubmit={handleSubmit} onClick={(e) => e.stopPropagation()}
        className="bg-gray-900 border border-gray-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[92vh] overflow-y-auto">
        <header className="sticky top-0 bg-gray-900 border-b border-gray-800 px-4 py-3 flex items-start justify-between">
          <div>
            <h3 className="font-bold text-base text-white inline-flex items-center gap-2">
              <UserX size={16} className="text-red-400" /> Déclarer une absence
            </h3>
            <div className="text-[10px] text-gray-500 mt-0.5">
              Notifie étudiants + parents + surveillance + scolarité
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={18} />
          </button>
        </header>

        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-300 mb-1">Date de l'absence *</label>
            <input type="date" value={date} required onChange={(e) => setDate(e.target.value)}
              min={new Date().toISOString().slice(0, 10)}
              className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100" />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-gray-300 mb-1">Classe</label>
              <input value={classe} onChange={(e) => setClasse(e.target.value.toUpperCase())}
                placeholder="L1MI"
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-300 mb-1">Matière</label>
              <input value={matiere} onChange={(e) => setMatiere(e.target.value)}
                placeholder="Optionnel"
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-semibold text-gray-300 mb-1">Heure début</label>
              <input type="time" value={heureDebut} onChange={(e) => setHeureDebut(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-300 mb-1">Heure fin</label>
              <input type="time" value={heureFin} onChange={(e) => setHeureFin(e.target.value)}
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-300 mb-1">Motif *</label>
            <textarea value={motif} onChange={(e) => setMotif(e.target.value)}
              rows={3} maxLength={2000} required
              placeholder="Ex : maladie, déplacement professionnel…"
              className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100 resize-none" />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-300 mb-1">Rattrapage</label>
            <div className="grid grid-cols-3 gap-1.5">
              {([
                { v: 'oui', l: 'Oui' },
                { v: 'non', l: 'Non' },
                { v: 'a_decider', l: 'À décider' },
              ] as const).map(o => (
                <button key={o.v} type="button" onClick={() => setPeutRattraper(o.v)}
                  className={`px-2 py-1.5 text-xs rounded border ${
                    peutRattraper === o.v
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-gray-800 text-gray-300 border-gray-700'
                  }`}>
                  {o.l}
                </button>
              ))}
            </div>
          </div>

          {peutRattraper === 'oui' && (
            <div>
              <label className="block text-xs font-semibold text-gray-300 mb-1">Date proposée</label>
              <input type="date" value={dateRattrapage} onChange={(e) => setDateRattrapage(e.target.value)}
                min={date}
                className="w-full px-3 py-2 text-sm bg-gray-800 border border-gray-700 rounded text-gray-100" />
            </div>
          )}

          {erreur && (
            <div className="bg-red-900/30 border border-red-800 text-red-200 rounded p-2 text-xs">
              {erreur}
            </div>
          )}
        </div>

        <footer className="sticky bottom-0 bg-gray-900 border-t border-gray-800 px-4 py-3 flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-800 rounded">
            Annuler
          </button>
          <button type="submit" disabled={isLoading || !motif.trim()}
            className="px-4 py-1.5 text-xs bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-semibold rounded inline-flex items-center gap-1.5">
            {isLoading ? <Loader2 size={12} className="animate-spin" /> : <UserX size={12} />}
            {isLoading ? 'Envoi…' : "Déclarer l'absence"}
          </button>
        </footer>
      </form>
    </div>
  );
}
